import type { PersonalizedItineraryOutput } from '@/app/lib/types';
import type { ItineraryFormValues } from '@/components/itinerary/ItineraryForm';

type PreBuiltItinerary = {
  slug: string;
  itinerary: PersonalizedItineraryOutput;
  formValues: Omit<ItineraryFormValues, 'date'> & { location: string };
};

export const preBuiltItineraries: PreBuiltItinerary[] = [
  {
    slug: 'highland-fling',
    formValues: {
      preferences: 'Iconic highland lochs and dramatic scenery.',
      skillLevel: 'intermediate',
      duration: 3,
      location: 'Scottish Highlands',
    },
    itinerary: {
      title: 'A Highland Fling',
      days: [
        {
          day: 1,
          title: 'Loch Ness Legend',
          activities: [
            {
              time: 'Morning',
              description: 'Start your adventure at the famous Loch Ness. Take a dip in the deep, mysterious waters near Dores Beach. Keep an eye out for Nessie! The water is cold, so a wetsuit is highly recommended.',
              locationName: 'Dores Beach, Loch Ness',
              locationSlug: 'loch-ness',
              category: 'swim',
              coordinates: { lat: 57.3873, lng: -4.3758 },
            },
            {
              time: 'Afternoon',
              description: 'Drive along the loch to explore Urquhart Castle. While swimming isn\'t allowed right at the castle, the views are spectacular and offer great photo opportunities of your morning swim spot.',
              locationName: 'Urquhart Castle',
              locationSlug: 'loch-ness',
              category: 'walk',
              coordinates: { lat: 57.3239, lng: -4.4524 },
            },
            {
              time: 'Evening',
              description: 'Enjoy a hearty meal at a traditional pub in Drumnadrochit.',
              locationName: 'Drumnadrochit',
              locationSlug: 'drumnadrochit',
              category: 'food',
              coordinates: { lat: 57.335, lng: -4.48 },
            }
          ],
        },
        {
          day: 2,
          title: 'Glen Coe Grandeur',
          activities: [
            {
              time: 'Morning',
              description: 'Swim in Loch Achtriochtan, a stunning loch set dramatically at the foot of the Aonach Eagach ridge in Glen Coe. The access is easy from the road, making it a perfect spot for a quick, breathtaking plunge.',
              locationName: 'Loch Achtriochtan, Glen Coe',
              locationSlug: 'glencoe-lochan',
              category: 'swim',
              coordinates: { lat: 56.6713, lng: -5.0001 },
            },
            {
              time: 'Afternoon',
              description: 'Hike to the Lost Valley (Coire Gabhail) and discover the pools of the River Coe. These are smaller, more sheltered spots for a wild swim, surrounded by epic mountain scenery.',
              locationName: 'River Coe Pools, Lost Valley',
              locationSlug: 'river-coe-pools',
              category: 'walk',
              coordinates: { lat: 56.6575, lng: -4.9752 },
            },
          ],
        },
        {
          day: 3,
          title: 'The Road to the Isles',
          activities: [
            {
              time: 'Morning',
              description: 'Head towards the coast and swim at the Camusdarach Beach, famous for its white sands and turquoise waters, featured in the film "Local Hero". It offers a completely different swimming experience to the lochs.',
              locationName: 'Camusdarach Beach, near Arisaig',
              locationSlug: 'camusdarach-beach',
              category: 'swim',
              coordinates: { lat: 56.9538, lng: -5.8453 },
            },
             {
              time: 'Afternoon',
              description: 'Discover the hidden gem of An Carloch, a small, secret loch with white sandy shores just inland from the coast. A magical final swim to end your Highland adventure.',
              locationName: 'An Carloch',
              locationSlug: 'ancarloch',
              category: 'swim',
              coordinates: { lat: 56.9380, lng: -5.8190 },
            },
          ],
        },
      ],
    },
  },
  {
    slug: 'skye-magical-waters',
    formValues: {
      preferences: 'Exploring the magical and mythical waters of Skye.',
      skillLevel: 'beginner',
      duration: 2,
      location: 'Isle of Skye',
    },
    itinerary: {
      title: 'Skye\'s Magical Waters',
      days: [
        {
          day: 1,
          title: 'The Famous Fairy Pools',
          activities: [
            {
              time: 'Morning',
              description: 'A must-do on Skye. Hike up the River Brittle and take your pick of the crystal-clear, emerald-green pools. The water is icy cold but incredibly refreshing. Go early to avoid the crowds.',
              locationName: 'Fairy Pools, Glen Brittle',
              locationSlug: 'fairy-pools',
              category: 'swim',
              coordinates: { lat: 57.2508, lng: -6.2583 },
            },
            {
              time: 'Afternoon',
              description: 'Warm up with a visit to the nearby Talisker Distillery for a tour and a dram of single malt whisky. A perfect Skye afternoon!',
              locationName: 'Talisker Distillery, Carbost',
              locationSlug: 'talisker-bay',
              category: 'other',
              coordinates: { lat: 57.2994, lng: -6.3562 },
            },
          ],
        },
        {
          day: 2,
          title: 'Coral Beaches & Castle Views',
          activities: [
            {
              time: 'Morning',
              description: 'Swim at Claigan Coral Beach. Despite the name, the beach is made from calcified seaweed. The water is incredibly clear and has a Caribbean feel on a sunny day. It\'s a short, flat walk from the car park.',
              locationName: 'Claigan Coral Beach',
              locationSlug: 'claigan-coral-beach',
              category: 'swim',
              coordinates: { lat: 57.5134, lng: -6.5934 },
            },
            {
              time: 'Afternoon',
              description: 'Head to the Sligachan Bridge for a final dip. Legend says that dipping your face in the water under the bridge grants you eternal beauty. It\'s a beautiful spot with the Cuillins as a backdrop.',
              locationName: 'Sligachan Old Bridge',
              locationSlug: 'sligachan-bridge',
              category: 'swim',
              coordinates: { lat: 57.2917, lng: -6.1764 },
            },
          ],
        },
      ],
    },
  },
];
